// ============================================================
// Offline Storage Service - IndexedDB wrapper for offline mode
// ============================================================

const DB_NAME = 'risq-navigator-db';
const DB_VERSION = 1;

const STORES = {
  inspections: 'inspections',
  answers: 'answers',
  evidence: 'evidence',
  syncQueue: 'syncQueue',
  vesselCache: 'vesselCache',
} as const;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORES.inspections)) {
        db.createObjectStore(STORES.inspections, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.answers)) {
        const answerStore = db.createObjectStore(STORES.answers, { keyPath: 'id', autoIncrement: true });
        answerStore.createIndex('inspectionId', 'inspectionId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.evidence)) {
        const evidenceStore = db.createObjectStore(STORES.evidence, { keyPath: 'id', autoIncrement: true });
        evidenceStore.createIndex('inspectionId', 'inspectionId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.syncQueue)) {
        db.createObjectStore(STORES.syncQueue, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.vesselCache)) {
        db.createObjectStore(STORES.vesselCache, { keyPath: 'id' });
      }
    };
  });
}

async function getAll<T>(storeName: string): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

async function get<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

async function put<T>(storeName: string, data: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.put(data);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function del(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function clear(storeName: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// --- Public API ---
export const offlineDB = {
  // Inspections
  async saveInspection(inspection: any) {
    await put(STORES.inspections, { ...inspection, _offline: true, _lastModified: new Date().toISOString() });
    await this.addToSyncQueue({ type: 'UPDATE_INSPECTION', payload: inspection });
  },

  async getInspection(id: string) {
    return get(STORES.inspections, id);
  },

  async getAllInspections() {
    return getAll(STORES.inspections);
  },

  // Answers (question responses)
  async saveAnswer(answer: { inspectionId: string; questionId: string; result: string; value?: string; notes?: string; photos: string[]; timestamp: string }) {
    await put(STORES.answers, { ...answer, id: `${answer.inspectionId}-${answer.questionId}`, _offline: true });
    await this.addToSyncQueue({ type: 'SAVE_ANSWER', payload: answer });
  },

  async getAnswers(inspectionId: string) {
    const db = await openDB();
    return new Promise<any[]>((resolve, reject) => {
      const tx = db.transaction(STORES.answers, 'readonly');
      const store = tx.objectStore(STORES.answers);
      const index = store.index('inspectionId');
      const request = index.getAll(inspectionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  // Evidence (photos, files)
  async saveEvidence(evidence: any) {
    await put(STORES.evidence, { ...evidence, _offline: true });
    await this.addToSyncQueue({ type: 'SAVE_EVIDENCE', payload: evidence });
  },

  async getEvidence(inspectionId: string) {
    const db = await openDB();
    return new Promise<any[]>((resolve, reject) => {
      const tx = db.transaction(STORES.evidence, 'readonly');
      const store = tx.objectStore(STORES.evidence);
      const index = store.index('inspectionId');
      const request = index.getAll(inspectionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  // Sync queue
  async addToSyncQueue(action: { type: string; payload: any }) {
    await put(STORES.syncQueue, { ...action, timestamp: new Date().toISOString(), synced: false });
  },

  async getSyncQueue() {
    return getAll(STORES.syncQueue);
  },

  async clearSyncQueue() {
    await clear(STORES.syncQueue);
  },

  // Vessel cache
  async cacheVessel(vessel: any) {
    await put(STORES.vesselCache, { ...vessel, _cachedAt: new Date().toISOString() });
  },

  async getCachedVessel(id: string) {
    return get(STORES.vesselCache, id);
  },

  async cacheAllVessels(vessels: any[]) {
    for (const vessel of vessels) {
      await this.cacheVessel(vessel);
    }
  },

  async getAllCachedVessels() {
    return getAll(STORES.vesselCache);
  },

  // Clear all
  async clearAll() {
    await clear(STORES.inspections);
    await clear(STORES.answers);
    await clear(STORES.evidence);
    await clear(STORES.syncQueue);
    await clear(STORES.vesselCache);
  },
};

// --- Sync Manager ---
export const syncManager = {
  async syncPendingChanges(): Promise<{ synced: number; failed: number }> {
    const queue = await offlineDB.getSyncQueue();
    let synced = 0;
    let failed = 0;

    for (const item of queue) {
      try {
        // In real app, this would make an API call
        // For demo, we simulate success
        await new Promise(r => setTimeout(r, 100));
        synced++;
      } catch {
        failed++;
      }
    }

    if (synced > 0) {
      await offlineDB.clearSyncQueue();
    }

    return { synced, failed };
  },
};
